import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './App.css';
import PiscineList from './components/PiscineList';
import CinemasSection from './components/CinemasSection';
import Map from './components/Map';
import GeolocationStatus from './components/GeolocationStatus';
import { heureVersMinutes, estOuvertA } from './utils/horaires';
import { dateISO, libelleJour } from './utils/dates';
import { API_BASE } from './utils/api';

// Paris.fr publie les horaires sur une dizaine de jours; on plafonne par
// sécurité au nombre de jours réellement présents dans les données reçues.
const MAX_JOURS = 10;

// Heure actuelle arrondie à la demi-heure inférieure (pour matcher les
// options du sélecteur, qui va par tranches de 30 minutes).
function heureActuelle() {
  const now = new Date();
  const minutes = now.getMinutes() < 30 ? '00' : '30';
  return `${String(now.getHours()).padStart(2, '0')}:${minutes}`;
}

// Options du sélecteur d'heure: 00:00, 00:30, 01:00, ..., 23:30.
const CRENEAUX_HEURE = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
});

// Rayons proposés en puces (plutôt qu'un curseur continu): assez peu
// d'options pour rester lisible sur une ligne, en couvrant du plus proche
// (marche à pied vers un cinéma) au plus large (recherche de piscine).
const RAYONS_KM = [0.5, 1, 2, 3, 5];
const libelleRayon = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km} km`);

function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function App() {
  // Piscines et cinémas sont deux sites distincts (piscines.mrpink200 et
  // cinema.mrpink200), chacun avec son propre nom de domaine et sa propre
  // page — plus de bouton pour basculer de l'un à l'autre au sein de l'app.
  // Le chemin /cinema reste reconnu par compatibilité avec d'anciens liens.
  const location = useLocation();
  const isCinemaHost = window.location.hostname === 'cinema.mrpink200.familyds.org';
  const mode = isCinemaHost || location.pathname === '/cinema' ? 'cinema' : 'piscine';

  useEffect(() => {
    document.title = mode === 'cinema' ? 'Cinémas à proximité' : 'Piscines à proximité';
    // La classe sur <html> pilote la palette et la typographie du thème
    // (voir les variables --accent/--gradient-*/--heading-font dans App.css).
    document.documentElement.classList.remove('piscine', 'cinema');
    document.documentElement.classList.add(mode);

    // "Ajouter à l'écran d'accueil" (iOS/Android) lit le manifest, l'icône
    // et le nom au moment où l'utilisateur l'ajoute, pas au build: comme les
    // deux sites partagent le même bundle statique, ces balises doivent être
    // réécrites ici selon le mode plutôt que fixées une fois pour toutes.
    const manifestHref = mode === 'cinema' ? '/manifest-cinema.json' : '/manifest.json';
    const iconHref = mode === 'cinema' ? '/icons/apple-touch-icon-cinema.png' : '/icons/apple-touch-icon-piscine.png';
    const appTitle = mode === 'cinema' ? 'Cinémas Paris' : 'Piscines Paris';
    const themeColor = mode === 'cinema' ? '#5C0621' : '#0E7490';

    document.getElementById('link-manifest')?.setAttribute('href', manifestHref);
    document.getElementById('link-apple-icon')?.setAttribute('href', iconHref);
    document.getElementById('link-favicon')?.setAttribute('href', iconHref);
    document.getElementById('meta-apple-title')?.setAttribute('content', appTitle);
    document.getElementById('meta-theme-color')?.setAttribute('content', themeColor);
  }, [mode]);

  const accentColor = mode === 'cinema' ? '#9D174D' : '#0E7490';

  const [piscines, setPiscines] = useState([]);
  const [filteredPiscines, setFilteredPiscines] = useState([]);
  const [cinemas, setCinemas] = useState([]);
  const [filteredCinemas, setFilteredCinemas] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Rayons distincts par mode: à pied pour aller au cinéma, on cherche
  // beaucoup plus court qu'un trajet acceptable jusqu'à une piscine.
  const [poolRadius, setPoolRadius] = useState(2); // km
  const [cinemaRadius, setCinemaRadius] = useState(1); // km
  const [desiredTime, setDesiredTime] = useState(heureActuelle);
  const [dayOffset, setDayOffset] = useState(0); // 0 = aujourd'hui, 1 = demain, ...
  const [showClosed, setShowClosed] = useState(false);
  const [radiusOpen, setRadiusOpen] = useState(false);
  const [locationLabel, setLocationLabel] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState(false);

  // Récupérer les piscines du serveur
  useEffect(() => {
    const fetchPiscines = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/piscines`);
        const data = await response.json();
        setPiscines(data);
      } catch (err) {
        setError('Erreur lors du chargement des piscines');
        console.error('Error fetching piscines:', err);
      }
    };

    fetchPiscines();
  }, []);

  // Récupérer la liste des cinémas parisiens (mode "je cherche un cinéma")
  useEffect(() => {
    fetch(`${API_BASE}/api/cinemas`)
      .then(r => r.json())
      .then(setCinemas)
      .catch(err => console.error('Erreur chargement cinémas:', err));
  }, []);

  // Récupérer la géolocalisation de l'utilisateur
  const actualiserPosition = () => {
    if (!navigator.geolocation) {
      setError('La géolocalisation n\'est pas disponible');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude, isDefault: false });
        setLoading(false);
      },
      (err) => {
        console.error('Geolocation error:', err);
        setError('Impossible d\'obtenir votre position');
        // Utiliser Paris comme position par défaut
        setUserLocation({ latitude: 48.8566, longitude: 2.3522, isDefault: true });
        setLoading(false);
      },
      // On force une position fraîche (pas de cache navigateur) et la
      // meilleure précision possible, sinon le bouton "actualiser" pourrait
      // renvoyer la même position mise en cache par le navigateur/l'OS.
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    );
  };

  useEffect(() => {
    actualiserPosition();
  }, []);

  // Géocoder une adresse saisie manuellement (alternative à la géolocalisation
  // du navigateur), via l'API officielle adresse.gouv.fr.
  const chercherAdresse = async (query) => {
    setGeocoding(true);
    setGeocodeError(false);

    try {
      const response = await fetch(`${API_BASE}/api/geocode?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      if (data.found) {
        setUserLocation({ latitude: data.latitude, longitude: data.longitude, isDefault: false });
      } else {
        setGeocodeError(true);
      }
    } catch (err) {
      console.error('Erreur géocodage adresse:', err);
      setGeocodeError(true);
    } finally {
      setGeocoding(false);
    }
  };

  // Traduire les coordonnées en description lisible (nom de rue, sans le
  // numéro) via l'API officielle adresse.gouv.fr, plutôt que d'afficher des
  // chiffres bruts à l'utilisateur.
  useEffect(() => {
    if (!userLocation) return;

    setLocationLabel(null);
    const controller = new AbortController();

    fetch(`${API_BASE}/api/reverse-geocode?lat=${userLocation.latitude}&lon=${userLocation.longitude}`, {
      signal: controller.signal
    })
      .then(r => r.json())
      .then(data => setLocationLabel(data.label || null))
      .catch(() => {}); // on garde les coordonnées en repli, cf. GeolocationStatus

    return () => controller.abort();
  }, [userLocation]);

  // Calculer les distances et filtrer les piscines
  useEffect(() => {
    if (!userLocation || piscines.length === 0) return;

    const selectedDate = dateISO(dayOffset);

    const piscinesTour = piscines.map(p => ({
      ...p,
      distance: calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        p.latitude,
        p.longitude
      ),
      horaireJour: (p.horaires && p.horaires[selectedDate]) || p.horaireAujourdhui || 'Horaires non disponibles'
    }));

    const heureMinutes = heureVersMinutes(desiredTime);
    const estFerme = (p) => estOuvertA(p.horaireJour, heureMinutes) === false;

    const filtered = piscinesTour
      .filter(p => p.distance <= poolRadius)
      .filter(p => showClosed || !estFerme(p))
      // Piscines fermées regroupées en fin de liste plutôt que mélangées
      // par distance, pour ne pas casser le rythme de lecture avec des
      // cartes fermées intercalées entre des cartes ouvertes.
      .sort((a, b) => {
        const fa = estFerme(a) ? 1 : 0;
        const fb = estFerme(b) ? 1 : 0;
        return fa !== fb ? fa - fb : a.distance - b.distance;
      });

    setFilteredPiscines(filtered);
  }, [userLocation, piscines, poolRadius, desiredTime, dayOffset, showClosed]);

  // Calculer les distances et filtrer les cinémas (mode "je cherche un cinéma").
  useEffect(() => {
    if (!userLocation || cinemas.length === 0) return;

    const filtered = cinemas
      .map(c => ({
        ...c,
        distance: calculateDistance(userLocation.latitude, userLocation.longitude, c.latitude, c.longitude)
      }))
      .filter(c => c.distance <= cinemaRadius)
      .sort((a, b) => a.distance - b.distance);

    setFilteredCinemas(filtered);
  }, [userLocation, cinemas, cinemaRadius]);

  // Nombre de jours réellement disponibles dans les données reçues du serveur.
  const joursDisponibles = piscines.reduce((max, p) => {
    const n = p.horaires ? Object.keys(p.horaires).length : 0;
    return Math.max(max, n);
  }, 1);
  const nbJours = Math.min(joursDisponibles, MAX_JOURS);

  // Adapter la forme des cinémas proches au format attendu par CinemasSection
  // (le même composant que celui utilisé dans le détail d'une piscine).
  const cinemasPourSection = filteredCinemas.map(c => ({
    nom: c.nom,
    url: `https://paris-cine.info/?cine=${c.id}`,
    distance_m: Math.round(c.distance * 1000),
    minutes_marche: Math.max(1, Math.round((c.distance * 1000) / 80))
  }));

  return (
    <div className="App">
      <header className="app-header">
        <h1>{mode === 'cinema' ? 'Cinémas à proximité' : 'Piscines à proximité'}</h1>
      </header>

      <main className="app-main">
        <div className="controls">
          <div className="pr-row">
            <GeolocationStatus
              userLocation={userLocation}
              loading={loading}
              error={error}
              locationLabel={locationLabel}
              geocoding={geocoding}
              geocodeError={geocodeError}
              onSearchAddress={chercherAdresse}
              onRefreshPosition={actualiserPosition}
            />
            {!loading && userLocation && (
              <button
                type="button"
                className="radius-pill"
                onClick={() => setRadiusOpen(o => !o)}
                aria-expanded={radiusOpen}
              >
                <span aria-hidden="true">🎯</span>
                <span>{libelleRayon(mode === 'piscine' ? poolRadius : cinemaRadius)}</span>
                <span className={`radius-chevron${radiusOpen ? ' radius-chevron--open' : ''}`} aria-hidden="true" />
              </button>
            )}
          </div>

          {!loading && userLocation && radiusOpen && (
            <div className="chip-row">
              {RAYONS_KM.map(r => (
                <button
                  key={r}
                  type="button"
                  className={`chip${(mode === 'piscine' ? poolRadius : cinemaRadius) === r ? ' chip--active' : ''}`}
                  onClick={() => {
                    (mode === 'piscine' ? setPoolRadius : setCinemaRadius)(r);
                    setRadiusOpen(false);
                  }}
                >
                  {libelleRayon(r)}
                </button>
              ))}
            </div>
          )}

          {!loading && userLocation && (
            <>
              <div className="row2">
                <div className="select-pill">
                  <span className="select-icon" aria-hidden="true">📅</span>
                  <select
                    value={dayOffset}
                    onChange={(e) => setDayOffset(parseInt(e.target.value, 10))}
                    aria-label={mode === 'piscine' ? 'Je veux nager' : 'Je veux voir un film'}
                  >
                    {Array.from({ length: nbJours }, (_, i) => i).map(offset => (
                      <option key={offset} value={offset}>{libelleJour(offset)}</option>
                    ))}
                  </select>
                  <span className="select-chevron" aria-hidden="true" />
                </div>
                <div className="select-pill">
                  <span className="select-icon" aria-hidden="true">🕐</span>
                  <select
                    value={desiredTime}
                    onChange={(e) => setDesiredTime(e.target.value)}
                    aria-label="Heure"
                  >
                    {CRENEAUX_HEURE.map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                  <span className="select-chevron" aria-hidden="true" />
                </div>
              </div>

              {mode === 'piscine' && (
                <label className="control-checkbox">
                  <input
                    type="checkbox"
                    checked={showClosed}
                    onChange={(e) => setShowClosed(e.target.checked)}
                  />
                  Afficher aussi les piscines fermées
                </label>
              )}
            </>
          )}
        </div>

        {!loading && userLocation && (
          <>
            {mode === 'piscine' ? (
              filteredPiscines.length > 0 ? (
                <>
                  <Map
                    userLocation={userLocation}
                    lieux={filteredPiscines}
                    searchRadius={poolRadius}
                    accentColor={accentColor}
                  />
                  <PiscineList
                    piscines={filteredPiscines}
                    userLocation={userLocation}
                    desiredTime={desiredTime}
                    jourLabel={libelleJour(dayOffset)}
                    selectedDate={dateISO(dayOffset)}
                  />
                </>
              ) : (
                <div className="no-results">
                  <p>Aucune piscine ouverte {libelleJour(dayOffset).toLowerCase()} à {desiredTime} dans un rayon de {poolRadius} km</p>
                  <p>Essayez d'augmenter le rayon, ou de changer l'heure ou le jour</p>
                </div>
              )
            ) : (
              filteredCinemas.length > 0 ? (
                <>
                  <Map
                    userLocation={userLocation}
                    lieux={filteredCinemas}
                    searchRadius={cinemaRadius}
                    accentColor={accentColor}
                  />
                  <div className="cinemas-mode-section">
                    <CinemasSection
                      cinemas={cinemasPourSection}
                      day={dateISO(dayOffset)}
                      desiredTime={desiredTime}
                    />
                  </div>
                </>
              ) : (
                <div className="no-results">
                  <p>Aucun cinéma dans un rayon de {cinemaRadius} km</p>
                  <p>Essayez d'augmenter le rayon de recherche</p>
                </div>
              )
            )}
          </>
        )}
      </main>

      <footer className="app-footer">
        <p>💡 Données actualisées depuis Paris.fr</p>
        <p>📍 Utilise votre géolocalisation pour les résultats</p>
      </footer>
    </div>
  );
}

export default App;
